import { useEffect, useId, useRef } from 'react';
import { navegacion } from '../ui/navegacion';
import { useCapa } from '../ui/navegacionReact';

// El diálogo, con teclado. Uno solo para los nueve que había.
//
// EL FALLO. Los nueve modales del sistema eran un `<div>` encima de otro:
// sin `role`, sin nombre, sin Escape y sin tocar el foco. Para quien no
// usa ratón eso significaba tres cosas, todas malas:
//
//   1. Al abrirse, el foco se quedaba DETRÁS. Tabulabas por la pantalla
//      tapada, sin ver dónde estabas, mientras el diálogo esperaba.
//   2. No había forma de cerrarlo con el teclado: la única salida era la
//      «X», a la que no se llegaba por lo anterior. Con `pedirMotivo`
//      activo —dar de baja a alguien, cambiar un rol— eso deja al usuario
//      encerrado en una acción que no puede completar ni abandonar.
//   3. Un lector de pantalla no anunciaba nada: sin `role="dialog"` no
//      hay diálogo, solo texto que apareció de la nada.
//
// Se arregla aquí una vez y no nueve, por lo mismo que «tocar un filtro
// vuelve a la página 1» acabó dentro de `useListaUrl`: la regla que se
// repite es la que se olvida en uno de los sitios.
const FOCALIZABLES = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

// El COMPORTAMIENTO, aparte de la carcasa. `Modal` cubre los ocho
// diálogos que caben en la caja de siempre; el formulario de producto
// tiene su propio armazón —cuerpo con desplazamiento y pie fijo, para que
// once campos quepan sin perder los botones de vista— y necesita lo
// mismo del teclado sin heredar la caja. Duplicar el encierro del foco
// para un solo caso es justo como se desincronizan estas cosas.
//
// Y EL BOTÓN «ATRÁS» · TC-DR-AT (3 oct 2026). Un diálogo es una CAPA: tapa
// la pantalla, así que atrás lo cierra y nada más, igual que Escape. Se
// registra aquí una vez y no en los ciento siete sitios que abren un
// modal. Dos opciones, las dos encendidas de fábrica:
//
//   capa     el diálogo ocupa una entrada del historial mientras está
//            abierto. Solo la apaga «¿Salir sin guardar?», que es la
//            pregunta del propio botón atrás.
//   guardia  si alguien escribió en un campo del diálogo, atrás pregunta
//            antes de cerrarlo. La «X» y Escape siguen cerrando sin
//            preguntar, como siempre. No cuentan los buscadores
//            (`type="search"`) ni lo que vaya dentro de `[data-sin-guardia]`.
export function useDialogo(onCerrar, { bloqueado = false, capa = true, guardia = true } = {}) {
  const panel = useRef(null);
  const idTitulo = useId();
  const tocado = useRef(false);
  const vigila = !!guardia;

  useCapa(capa, onCerrar, {
    bloqueada: bloqueado,
    // `guardia` puede ser además una función: lo que el diálogo tiene a
    // medias y no pasa por un campo (las fotos de una recepción).
    tieneCambios: guardia
      ? () => tocado.current || (typeof guardia === 'function' && !!guardia())
      : undefined,
  });

  useEffect(() => {
    const nodo = panel.current;
    if (!vigila || !nodo) return undefined;
    const marcar = (e) => {
      const campo = e.target;
      if (campo?.type === 'search' || campo?.closest?.('[data-sin-guardia]')) return;
      tocado.current = true;
    };
    nodo.addEventListener('input', marcar, true);
    nodo.addEventListener('change', marcar, true);
    return () => {
      nodo.removeEventListener('input', marcar, true);
      nodo.removeEventListener('change', marcar, true);
    };
  }, [vigila]);

  useEffect(() => {
    // A dónde volver al cerrar. Sin esto el foco cae al principio del
    // documento y hay que recorrer el menú entero para retomar el trabajo
    // donde se dejó.
    const previo = document.activeElement;

    // El foco entra al diálogo. Al panel y no al primer campo: así el
    // lector de pantalla lee el título antes que la primera etiqueta, y
    // se entiende qué se abrió.
    //
    // Salvo que el contenido ya haya reclamado el foco con `autoFocus` —el
    // motivo obligatorio de una baja lo hace—: quien lo declaró sabe mejor
    // que este componente dónde empieza el trabajo, y robárselo obligaría
    // a tabular hasta el único campo que hay.
    if (!panel.current?.contains(document.activeElement)) panel.current?.focus();

    const alTeclear = (e) => {
      // Con «¿Salir sin guardar?» encima, el teclado es de esa pregunta:
      // el diálogo de debajo no cierra ni mueve el foco.
      if (capa && navegacion.confirmacion()) return;
      if (e.key === 'Escape' && !bloqueado) {
        e.stopPropagation();
        onCerrar();
        return;
      }
      if (e.key !== 'Tab') return;

      // Encierro del tabulador. Un diálogo del que el foco se escapa a la
      // pantalla de atrás es peor que no tenerlo: se sigue tecleando en
      // campos invisibles.
      const focos = [...(panel.current?.querySelectorAll(FOCALIZABLES) || [])]
        .filter((el) => el.offsetParent !== null);
      if (focos.length === 0) {
        e.preventDefault();
        panel.current?.focus();
        return;
      }
      const primero = focos[0];
      const ultimo = focos[focos.length - 1];
      const actual = document.activeElement;

      if (e.shiftKey && (actual === primero || actual === panel.current)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && actual === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };

    document.addEventListener('keydown', alTeclear, true);
    // El fondo no debe desplazarse bajo el diálogo: en móvil, arrastrar
    // dentro del modal movía la lista de atrás y al cerrar aparecías en
    // otro sitio de la tabla.
    const desbordePrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = desbordePrevio;
      if (previo instanceof HTMLElement) previo.focus();
    };
  }, [onCerrar, bloqueado, capa]);

  return {
    idTitulo,
    // Se derraman sobre el panel del diálogo, sea cual sea su maquetación.
    propsPanel: {
      ref: panel,
      role: 'dialog',
      'aria-modal': true,
      'aria-labelledby': idTitulo,
      tabIndex: -1,
    },
  };
}
